import { useConfig } from "@/context/ConfigContext";
import { filterNonEmptyCategories } from "@/utils/dataHelpers";
import { useEffect, useState } from "react";

/**
 * Hook untuk mengambil kategori produk dari API
 * @param {Object} options - Options untuk fetch
 * @param {number|string} options.id - ID kategori spesifik (optional)
 * @param {string} options.slug - Slug kategori spesifik (optional)
 * @param {boolean} options.enabled - Enable/disable fetch (default: true)
 */
export function useProductCategories(options = {}) {
  const { id, slug, params = {}, enabled = true } = options;
  const { config, configData } = useConfig();
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    // Pastikan config sudah ready dan team_id tersedia
    if (!enabled || !configData?.team_id || !config?.api?.baseUrl) {
      return;
    }

    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);

        const teamId = configData.team_id;

        // Gunakan proxy API route untuk menghindari CORS
        // API route akan otomatis mengambil baseUrl dari config berdasarkan domain
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append("page", params.page);
        if (params.per_page) queryParams.append("per_page", params.per_page);
        Object.entries(params).forEach(([key, value]) => {
          if (["page", "per_page"].includes(key)) return;
          if (value === undefined || value === null || value === "") return;
          queryParams.append(key, value);
        });
        const queryString = queryParams.toString();

        const categoryKey = slug || id;
        const proxyUrl = categoryKey
          ? `/api/products?team_id=${teamId}&type=categories&slug=${categoryKey}${
              queryString ? `&${queryString}` : ""
            }`
          : `/api/products?team_id=${teamId}&type=categories${
              queryString ? `&${queryString}` : ""
            }`;

        // Log request details untuk debugging
        // console.log("[useProductCategories] Fetching categories:", {
        //   proxyUrl,
        //   teamId,
        //   id,
        //   baseUrl: config?.api?.baseUrl,
        // });

        const response = await fetch(proxyUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          cache: "no-cache",
        });

        if (!response.ok) {
          let errorData;
          let errorText;

          try {
            errorText = await response.text();
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            // Jika bukan JSON, gunakan text sebagai error message
            errorData = {
              message: errorText || response.statusText,
              status: response.status,
            };
          }

          // Ambil apiUrl dari errorData jika ada (dikirim dari API route)
          const apiUrl = errorData.apiUrl || "N/A";

          // Build detailed error message
          const errorDetails = {
            proxyEndpoint: proxyUrl,
            laravelApiUrl: apiUrl, // URL lengkap Laravel API yang di-hit
            status: response.status,
            statusText: response.statusText,
            teamId: teamId,
            categoryId: id,
            categorySlug: slug,
            apiBaseUrl: config?.api?.baseUrl,
            errorMessage:
              errorData.message ||
              errorData.error ||
              errorText ||
              response.statusText,
            errorDetail: errorData.errorDetail || errorData.errorData,
            errorData: errorData,
          };

          console.error("[useProductCategories] API Error:", errorDetails);

          throw new Error(
            `API Error\n` +
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
              `Proxy Endpoint: ${proxyUrl}\n` +
              `Laravel API URL: ${apiUrl}\n` +
              `Status: ${response.status} ${response.statusText}\n` +
              `Team ID: ${teamId}\n` +
              `Category ID: ${id || "N/A"}\n` +
              `Category Slug: ${slug || "N/A"}\n` +
              `API Base URL: ${config?.api?.baseUrl}\n` +
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
              `Error Message: ${
                errorData.message ||
                errorData.error ||
                errorText ||
                response.statusText
              }\n` +
              `Error Detail: ${
                errorData.errorDetail
                  ? errorData.errorDetail
                  : JSON.stringify(errorData, null, 2)
              }`
          );
        }

        const data = await response.json();

        // Handle response berdasarkan apakah ada id atau tidak
        if (categoryKey) {
          // Single category
          setCategory(data.data || data);
        } else {
          // List categories - handle berbagai format response
          if (data.meta || data.pagination || data.data?.meta) {
            setPagination(data.meta || data.pagination || data.data?.meta);
          }

          let categoriesList = [];
          if (Array.isArray(data)) {
            categoriesList = data;
          } else if (data.data && Array.isArray(data.data)) {
            categoriesList = data.data;
          } else if (data.categories && Array.isArray(data.categories)) {
            categoriesList = data.categories;
          } else if (data.data && Array.isArray(data.data.data)) {
            categoriesList = data.data.data;
          }
          setCategories(filterNonEmptyCategories(categoriesList));
        }
      } catch (err) {
        console.error("[useProductCategories] Error fetching categories:", {
          message: err.message,
          stack: err.stack,
          teamId: configData?.team_id,
          categoryId: id,
          categorySlug: slug,
          baseUrl: config?.api?.baseUrl,
        });
        setError(err.message || "Gagal mengambil kategori produk");
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [
    configData?.team_id,
    config?.api?.baseUrl,
    id,
    slug,
    enabled,
    JSON.stringify(params),
  ]);

  const hasSingleCategory = !!(slug || id);

  return {
    categories: hasSingleCategory ? null : categories,
    category: hasSingleCategory ? category : null,
    loading,
    error,
    pagination,
  };
}
